import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

const NAV = [
  { key: 'datasets', label: 'Datasets',    icon: '🗂️' },
  { key: 'erd',      label: 'ERD Canvas',  icon: '🔗' },
  { key: 'json',     label: 'Raw JSON',    icon: '{ }' },
  { key: 'tables',   label: 'Tables',      icon: '📋' },
];
const ADMIN_NAV = [
  { key: 'users',   label: 'Users',        icon: '👥' },
  { key: 'upload',  label: 'Upload JSON',  icon: '⬆️' },
];

export default function Sidebar({ active, onNav, searchQuery, onSearchChange, hasDataset }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const isAdmin = user?.role === 'admin';

  const handleSearchChange = (e) => onSearchChange(e.target.value);
  const handleSearchKey = (e) => {
    if (e.key === 'Enter' && searchQuery.trim()) onNav('search');
  };

  return (
    <aside style={{
      width: 220, minHeight: '100vh', background: 'var(--surface)',
      borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column',
      padding: '20px 0', flexShrink: 0,
    }}>
      {/* Logo */}
      <div style={{ padding: '0 16px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{
          background: 'var(--logo-bg)', borderRadius: 8, padding: '8px 10px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <img src="/btechdata.jpeg" alt="Btechdata"
            style={{ display: 'block', maxWidth: '100%', width: 160, height: 'auto',
                     mixBlendMode: theme === 'light' ? 'multiply' : 'normal' }} />
        </div>
      </div>

      {/* Search */}
      <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ fontSize: 9, color: 'var(--muted)', marginBottom: 6, letterSpacing: '0.1em', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>SEARCH JSON</span>
          {searchQuery && (
            <button onClick={() => onSearchChange('')}
              style={{ background: 'transparent', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 11, padding: 0 }}>
              ✕
            </button>
          )}
        </div>
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKey}
            onFocus={() => searchQuery.trim() && onNav('search')}
            placeholder={hasDataset ? "e.g. pat, code, date…" : "select dataset first"}
            disabled={!hasDataset}
            style={{
              width: '100%', padding: '7px 10px 7px 26px', borderRadius: 7,
              background: 'var(--surface2)', border: '1px solid var(--border)',
              color: 'var(--text)', fontFamily: 'JetBrains Mono, monospace', fontSize: 11,
              outline: 'none',
              opacity: hasDataset ? 1 : 0.5,
              cursor: hasDataset ? 'text' : 'not-allowed',
            }}
          />
          <span style={{
            position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)',
            fontSize: 11, color: 'var(--muted)', pointerEvents: 'none',
          }}>⌕</span>
        </div>
        {searchQuery && (
          <div style={{ fontSize: 9, color: 'var(--muted)', marginTop: 6, fontStyle: 'italic' }}>
            LIKE '%{searchQuery}%' — case-insensitive
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: '12px 0', overflow: 'auto' }}>
        <div style={{ fontSize: 9, color: 'var(--muted)', padding: '8px 20px 4px', letterSpacing: '0.1em' }}>EXPLORE</div>
        {NAV.map(item => (
          <button key={item.key} onClick={() => onNav(item.key)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 20px', background: active === item.key ? 'rgba(56,189,248,0.08)' : 'transparent',
              border: 'none', borderLeft: active === item.key ? '2px solid var(--accent)' : '2px solid transparent',
              color: active === item.key ? 'var(--accent)' : 'var(--muted)',
              cursor: 'pointer', fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
              textAlign: 'left', transition: 'all 0.15s',
            }}>
            <span>{item.icon}</span><span>{item.label}</span>
          </button>
        ))}

        {isAdmin && (
          <>
            <div style={{ fontSize: 9, color: 'var(--muted)', padding: '14px 20px 4px', letterSpacing: '0.1em' }}>ADMIN</div>
            {ADMIN_NAV.map(item => (
              <button key={item.key} onClick={() => onNav(item.key)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                  padding: '9px 20px', background: active === item.key ? 'rgba(129,140,248,0.08)' : 'transparent',
                  border: 'none', borderLeft: active === item.key ? '2px solid var(--accent2)' : '2px solid transparent',
                  color: active === item.key ? 'var(--accent2)' : 'var(--muted)',
                  cursor: 'pointer', fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
                  textAlign: 'left', transition: 'all 0.15s',
                }}>
                <span>{item.icon}</span><span>{item.label}</span>
              </button>
            ))}
          </>
        )}
      </nav>

      {/* User info */}
      <div style={{ padding: '16px 20px', borderTop: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <div style={{
            width: 28, height: 28, borderRadius: '50%',
            background: isAdmin ? 'rgba(99,102,241,0.15)' : 'rgba(2,132,199,0.1)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, border: `1px solid ${isAdmin ? 'rgba(99,102,241,0.3)' : 'rgba(2,132,199,0.2)'}`,
            color: isAdmin ? 'var(--accent2)' : 'var(--accent)',
          }}>
            {user?.username?.[0]?.toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize: 11, color: 'var(--text)' }}>{user?.username}</div>
            <span className={`tag tag-${user?.role}`}>{user?.role}</span>
          </div>
        </div>

        {/* Theme toggle */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 10, padding: '4px 8px', background: 'var(--surface2)',
          border: '1px solid var(--border)', borderRadius: 8,
        }}>
          <span style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.06em' }}>THEME</span>
          <div style={{ display: 'flex', gap: 4 }}>
            <button
              onClick={() => theme !== 'dark' && toggle()}
              title="Dark mode"
              style={{
                background: theme === 'dark' ? 'var(--accent)' : 'transparent',
                color: theme === 'dark' ? 'var(--surface)' : 'var(--muted)',
                border: 'none', borderRadius: 5, cursor: 'pointer',
                padding: '3px 7px', fontSize: 11, transition: 'all 0.15s',
              }}>🌙</button>
            <button
              onClick={() => theme !== 'light' && toggle()}
              title="Light mode"
              style={{
                background: theme === 'light' ? 'var(--accent)' : 'transparent',
                color: theme === 'light' ? 'var(--surface)' : 'var(--muted)',
                border: 'none', borderRadius: 5, cursor: 'pointer',
                padding: '3px 7px', fontSize: 11, transition: 'all 0.15s',
              }}>☀️</button>
          </div>
        </div>

        <button className="btn btn-secondary" onClick={logout} style={{ width: '100%', justifyContent: 'center', fontSize: 11 }}>
          Sign Out
        </button>
      </div>
    </aside>
  );
}
